import styled from '@emotion/styled';
import { Package, MapPin, Calendar, Truck, ExternalLink } from 'lucide-react';
import type { Order, StatusValue } from '@/domain/entities/order.entity';
import { OrderStatusBadge } from './OrderStatusBadge';
import { OrderProgressStepper } from './OrderProgressStepper';
import { formatOrderDate, formatAddress, getOrderTotalQuantity } from './utils';

const CardContainer = styled.div`
  border: 1px solid ${({ theme }) => theme.colors.border.color};
  border-radius: ${({ theme }) => theme.radius.regular};
  background: ${({ theme }) => theme.colors.background.primary};
  overflow: hidden;
  margin-bottom: ${({ theme }) => theme.space.xl};
`;

const CardHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: ${({ theme }) => theme.space.md};
  padding: ${({ theme }) => theme.space.lg} ${({ theme }) => theme.space.xl};
  border-bottom: 1px solid ${({ theme }) => theme.colors.border.color};
  background: ${({ theme }) => theme.colors.background.secondary};
`;

const OrderNumberText = styled.span`
  font-size: ${({ theme }) => theme.fontSizes[3]}px;
  font-weight: ${({ theme }) => theme.typography.config.weight.bold};
  color: ${({ theme }) => theme.colors.text.primary};
`;

const StepperSection = styled.div`
  padding: ${({ theme }) => theme.space.md} ${({ theme }) => theme.space.xl};
`;

const TimelineSection = styled.div`
  padding: 0 ${({ theme }) => theme.space.xl} ${({ theme }) => theme.space.lg};
`;

const TimelineTitle = styled.h4`
  font-size: ${({ theme }) => theme.fontSizes[1]}px;
  font-weight: ${({ theme }) => theme.typography.config.weight.bold};
  color: ${({ theme }) => theme.colors.text.secondary};
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin: 0 0 ${({ theme }) => theme.space.md} 0;
`;

const TimelineList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
  position: relative;

  &::before {
    content: '';
    position: absolute;
    left: 5px;
    top: 8px;
    bottom: 8px;
    width: 2px;
    background: ${({ theme }) => theme.colors.border.color};
  }
`;

const TimelineItem = styled.li<{ readonly isLatest?: boolean }>`
  display: flex;
  align-items: flex-start;
  gap: ${({ theme }) => theme.space.md};
  padding: ${({ theme }) => theme.space.sm} 0;
  position: relative;
`;

const TimelineDot = styled.div<{ readonly isLatest?: boolean }>`
  width: 12px;
  height: 12px;
  min-width: 12px;
  border-radius: 50%;
  background: ${({ isLatest }) => (isLatest ? '#2f806a' : '#ccc')};
  z-index: 1;
  margin-top: 3px;
`;

const TimelineContent = styled.div`
  flex: 1;
`;

const TimelineStatus = styled.span<{ readonly isLatest?: boolean }>`
  font-size: ${({ theme }) => theme.fontSizes[2]}px;
  font-weight: ${({ isLatest, theme }) =>
    isLatest ? theme.typography.config.weight.bold : theme.typography.config.weight.regular};
  color: ${({ isLatest, theme }) =>
    isLatest ? theme.colors.text.primary : theme.colors.text.secondary};
  text-transform: capitalize;
`;

const TimelineDate = styled.span`
  font-size: ${({ theme }) => theme.fontSizes[1]}px;
  color: ${({ theme }) => theme.colors.text.tertiary};
  margin-left: ${({ theme }) => theme.space.sm};
`;

const DetailsGrid = styled.div`
  display: grid;
  grid-template-columns: auto 1fr;
  gap: ${({ theme }) => theme.space.xl};
  padding: 0 ${({ theme }) => theme.space.xl} ${({ theme }) => theme.space.xl};
  border-top: 1px solid ${({ theme }) => theme.colors.border.color};
  padding-top: ${({ theme }) => theme.space.lg};

  @media (max-width: ${({ theme }) => theme.mediaQueries.sm}) {
    grid-template-columns: 1fr;
  }
`;

const OrderItems = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.space.sm};
  align-items: center;
`;

const Thumbnail = styled.img`
  width: 56px;
  height: 70px;
  object-fit: cover;
  border-radius: ${({ theme }) => theme.radius.small};
  background: ${({ theme }) => theme.colors.background.secondary};
`;

const ItemCount = styled.span`
  font-size: ${({ theme }) => theme.fontSizes[1]}px;
  color: ${({ theme }) => theme.colors.text.secondary};
  margin-left: ${({ theme }) => theme.space.sm};
`;

const InfoGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: ${({ theme }) => theme.space.lg};

  @media (max-width: ${({ theme }) => theme.mediaQueries.sm}) {
    grid-template-columns: 1fr;
  }
`;

const InfoBlock = styled.div``;

const InfoLabel = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: ${({ theme }) => theme.fontSizes[1]}px;
  font-weight: ${({ theme }) => theme.typography.config.weight.bold};
  color: ${({ theme }) => theme.colors.text.secondary};
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: 4px;

  svg {
    width: 14px;
    height: 14px;
  }
`;

const InfoValue = styled.div`
  font-size: ${({ theme }) => theme.fontSizes[2]}px;
  color: ${({ theme }) => theme.colors.text.primary};
  line-height: 1.4;
`;

const CarrierBanner = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.space.md};
  padding: ${({ theme }) => theme.space.md} ${({ theme }) => theme.space.xl};
  background: rgba(10, 96, 155, 0.06);
  border-top: 1px solid ${({ theme }) => theme.colors.border.color};
  font-size: ${({ theme }) => theme.fontSizes[1]}px;
  color: ${({ theme }) => theme.colors.text.secondary};

  svg {
    width: 16px;
    height: 16px;
    color: #0a609b;
  }
`;

const ActionRow = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.space.md};
  padding: ${({ theme }) => theme.space.md} ${({ theme }) => theme.space.xl} ${({ theme }) => theme.space.lg};
`;

const ActionButton = styled.button`
  padding: ${({ theme }) => theme.space.sm} ${({ theme }) => theme.space.lg};
  background: ${({ theme }) => theme.colors.background.primary};
  border: 1px solid ${({ theme }) => theme.colors.text.primary};
  border-radius: ${({ theme }) => theme.radius.small};
  font-size: ${({ theme }) => theme.fontSizes[1]}px;
  font-weight: ${({ theme }) => theme.typography.config.weight.bold};
  color: ${({ theme }) => theme.colors.text.primary};
  cursor: pointer;
  transition: all 0.2s;
  font-family: inherit;
  letter-spacing: 0.5px;
  display: inline-flex;
  align-items: center;
  gap: 6px;

  &:hover {
    background: ${({ theme }) => theme.colors.text.primary};
    color: ${({ theme }) => theme.colors.background.primary};
  }

  svg {
    width: 14px;
    height: 14px;
  }
`;

const STATUS_LABELS: Record<string, string> = {
  unfulfilled: 'Order received',
  pending: 'Pending fulfillment',
  placed: 'Shipping label created',
  shipped: 'Shipped',
  partially_fulfilled: 'Partially shipped',
  fulfilled: 'Delivered',
  delivered: 'Delivered',
};

function formatStatusLabel(status: string): string {
  return STATUS_LABELS[status.toLowerCase()] ?? status.split('_').join(' ');
}

function formatTimelineDate(iso: string): string {
  try {
    const d = new Date(iso);
    return d.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    });
  } catch {
    return iso;
  }
}

function buildShippingTimeline(order: Order): StatusValue[] {
  const history = order.status_history?.shipping;
  if (history && history.length > 0) {
    return [...history].reverse();
  }

  return [
    {
      value: order.shipping_status.value,
      date_updated: order.shipping_status.date_updated,
    },
  ];
}

interface TrackOrderCardProps {
  readonly order: Order;
  readonly onViewDetails?: (orderId: string) => void;
}

export function TrackOrderCard({ order, onViewDetails }: TrackOrderCardProps) {
  const timeline = buildShippingTimeline(order);
  const totalQty = getOrderTotalQuantity(order);
  const thumbItems = order.line_items?.slice(0, 3) ?? [];

  return (
    <CardContainer>
      <CardHeader>
        <OrderNumberText>Order #{order.number}</OrderNumberText>
        <OrderStatusBadge order={order} />
      </CardHeader>

      <StepperSection>
        <OrderProgressStepper order={order} />
      </StepperSection>

      <TimelineSection>
        <TimelineTitle>Shipping Updates</TimelineTitle>
        <TimelineList>
          {timeline.map((entry, i) => (
            <TimelineItem key={`${entry.value}-${entry.date_updated}`} isLatest={i === 0}>
              <TimelineDot isLatest={i === 0} />
              <TimelineContent>
                <TimelineStatus isLatest={i === 0}>
                  {formatStatusLabel(entry.value)}
                </TimelineStatus>
                <TimelineDate>{formatTimelineDate(entry.date_updated)}</TimelineDate>
              </TimelineContent>
            </TimelineItem>
          ))}
        </TimelineList>
      </TimelineSection>

      <DetailsGrid>
        <OrderItems>
          {thumbItems.map((item) => (
            <Thumbnail
              key={item.id}
              src={item.image_url || 'https://placehold.co/56x70/f5f5f5/999?text=N/A'}
              alt={item.name}
              loading="lazy"
            />
          ))}
          <ItemCount>
            {totalQty} {totalQty === 1 ? 'item' : 'items'}
          </ItemCount>
        </OrderItems>

        <InfoGrid>
          <InfoBlock>
            <InfoLabel><Calendar size={14} /> Order Placed</InfoLabel>
            <InfoValue>{formatOrderDate(order.purchase_date || order.status.date_updated)}</InfoValue>
          </InfoBlock>
          <InfoBlock>
            <InfoLabel><MapPin size={14} /> Ship To</InfoLabel>
            <InfoValue>{formatAddress(order.shipping_address)}</InfoValue>
          </InfoBlock>
          <InfoBlock>
            <InfoLabel><Truck size={14} /> Shipping Method</InfoLabel>
            <InfoValue>{order.shipping_method?.title || 'Standard Shipping'}</InfoValue>
          </InfoBlock>
          <InfoBlock>
            <InfoLabel><Package size={14} /> Last Update</InfoLabel>
            <InfoValue>
              {formatTimelineDate(
                order.shipping_status.date_updated || order.last_update_date
              )}
            </InfoValue>
          </InfoBlock>
        </InfoGrid>
      </DetailsGrid>

      <CarrierBanner>
        <Truck size={16} />
        <span>
          Carrier tracking details will be available here once integrated with a shipping provider.
        </span>
      </CarrierBanner>

      {onViewDetails && (
        <ActionRow>
          <ActionButton onClick={() => onViewDetails(order.id)}>
            <ExternalLink size={14} />
            VIEW FULL ORDER
          </ActionButton>
        </ActionRow>
      )}
    </CardContainer>
  );
}
